import { orpc } from '@/api/rpc/client';
import { ProductModule } from '@erp/shared-types';
import type { QueryClient } from '@tanstack/react-query';
import { useGetModuleConfig } from './modules';

/**
 * Returns whether the org has opted into the new OMS experience.
 *
 * Backed by the `orderManagement.enhancedSalesOrderUi` flag on the
 * Order Management module config. When true, the org uses the OMS v3
 * pages at the unversioned paths (`/sales-orders`, `/discounts`); it also
 * opts the legacy drafts table into the EnhancedSalesOrderDraftModal.
 * Defaults to `false` when the module config or the flag is missing.
 */
export function useEnhancedSalesOrderUi() {
  const { data: moduleConfig, isLoading } = useGetModuleConfig(
    ProductModule.ORDER_MANAGEMENT,
  );

  const isEnhancedSalesOrderUiEnabled =
    moduleConfig?.config?.orderManagement?.enhancedSalesOrderUi ?? false;

  return { isEnhancedSalesOrderUiEnabled, isLoading };
}

/**
 * beforeLoad-safe check of the org's `enhancedSalesOrderUi` flag, for the
 * temporary redirects that keep legacy orgs on `/sales-orders-v2` and
 * `/discounts-v1`. Uses the same query options as `useGetModuleConfig` so
 * react-query dedupes with hook consumers. NOT_FOUND (no module-config row)
 * and transient errors resolve to `false` (legacy pages) rather than
 * blocking navigation.
 */
export async function fetchEnhancedSalesOrderUiEnabled(
  queryClient: QueryClient,
): Promise<boolean> {
  try {
    const moduleConfig = await queryClient.ensureQueryData(
      orpc.org.config.modules.getModuleConfig.queryOptions({
        input: { module: ProductModule.ORDER_MANAGEMENT },
      }),
    );
    return moduleConfig?.config?.orderManagement?.enhancedSalesOrderUi ?? false;
  } catch {
    return false;
  }
}
